import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Story, UserProfile } from '../types';
import { Plus, X, Music, Eye, Send, Volume2, VolumeX, Trash2, Edit3, Sparkles, Check, Smile } from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { ConfirmationModal } from './ConfirmationModal';

interface StoriesCarouselProps {
  currentUser: UserProfile;
  stories: Story[];
  onOpenCreateStory: () => void;
  onDeleteStory?: (storyId: string) => void;
  onEditStory?: (storyId: string, updates: { caption?: string; mood?: string; music?: string } | string) => void;
  onReplyStory?: (story: Story, text: string) => void;
}

interface UserStoriesGroup {
  username: string;
  displayName: string;
  userAvatar: string;
  stories: Story[];
}

export const StoriesCarousel: React.FC<StoriesCarouselProps> = ({
  currentUser,
  stories,
  onOpenCreateStory,
  onDeleteStory,
  onEditStory,
  onReplyStory,
}) => {
  const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);
  const [activeStoryIndex, setActiveStoryIndex] = useState<number>(0);
  const [replyText, setReplyText] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [storyToDelete, setStoryToDelete] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editMood, setEditMood] = useState<string | undefined>(undefined);
  const [editMusic, setEditMusic] = useState('');
  const [showConfirmEditModal, setShowConfirmEditModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [viewedStories, setViewedStories] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('wateen_viewed_stories');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Group stories by user
  const userGroups = useMemo(() => {
    const groupsMap = new Map<string, UserStoriesGroup>();
    stories.forEach((story) => {
      if (!groupsMap.has(story.username)) {
        groupsMap.set(story.username, {
          username: story.username,
          displayName: story.displayName,
          userAvatar: story.userAvatar,
          stories: [],
        });
      }
      groupsMap.get(story.username)!.stories.push(story);
    });

    // Sort: current user first if they have stories, then others
    const groupsArray = Array.from(groupsMap.values());
    const myGroupIdx = groupsArray.findIndex((g) => g.username === currentUser.username);
    if (myGroupIdx > -1) {
      const myGroup = groupsArray.splice(myGroupIdx, 1)[0];
      groupsArray.unshift(myGroup);
    }
    return groupsArray;
  }, [stories, currentUser.username]);

  const currentGroup = activeGroupIndex !== null ? userGroups[activeGroupIndex] : null;
  const currentStory = currentGroup ? currentGroup.stories[activeStoryIndex] : null;

  useEffect(() => {
    if (currentStory && !viewedStories.has(currentStory.id)) {
      const newViewed = new Set(viewedStories);
      newViewed.add(currentStory.id);
      setViewedStories(newViewed);
      localStorage.setItem('wateen_viewed_stories', JSON.stringify(Array.from(newViewed)));
    }
  }, [currentStory, viewedStories]);

  const handleNextStory = () => {
    if (!currentGroup) return;
    if (activeStoryIndex < currentGroup.stories.length - 1) {
      setActiveStoryIndex(activeStoryIndex + 1);
      setProgress(0);
    } else {
      // Go to next user
      if (activeGroupIndex !== null && activeGroupIndex < userGroups.length - 1) {
        setActiveGroupIndex(activeGroupIndex + 1);
        setActiveStoryIndex(0);
        setProgress(0);
      } else {
        closeViewer();
      }
    }
  };

  const handlePrevStory = () => {
    if (!currentGroup) return;
    if (activeStoryIndex > 0) {
      setActiveStoryIndex(activeStoryIndex - 1);
      setProgress(0);
    } else {
      // Go to prev user
      if (activeGroupIndex !== null && activeGroupIndex > 0) {
        setActiveGroupIndex(activeGroupIndex - 1);
        setActiveStoryIndex(userGroups[activeGroupIndex - 1].stories.length - 1);
        setProgress(0);
      }
    }
  };

  const closeViewer = () => {
    setActiveGroupIndex(null);
    setActiveStoryIndex(0);
    setProgress(0);
    setIsPaused(false);
  };

  const sendQuickReaction = (emoji: string) => {
    if (currentStory && onReplyStory) {
      onReplyStory(currentStory, emoji);
      soundEffects.playSent();
      closeViewer();
    }
  };

  // Auto-advance logic for images
  useEffect(() => {
    if (!currentStory || isPaused || currentStory.type === 'video') return;

    const DURATION = 5000;
    const UPDATE_INTERVAL = 50;

    progressTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + (UPDATE_INTERVAL / DURATION) * 100;
        if (nextProgress >= 100) {
          clearInterval(progressTimerRef.current);
          handleNextStory();
          return 0;
        }
        return nextProgress;
      });
    }, UPDATE_INTERVAL);

    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [currentStory, isPaused, activeStoryIndex, activeGroupIndex]); // re-run when these change

  const handleVideoTimeUpdate = () => {
    if (videoRef.current && !isPaused) {
      const { currentTime, duration } = videoRef.current;
      setProgress((currentTime / duration) * 100);
    }
  };

  const handleVideoEnded = () => {
    handleNextStory();
  };

  // Manage video pause state
  useEffect(() => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(console.error);
      }
    }
  }, [isPaused, currentStory]);

  return (
    <>
      <div className="p-3 sm:px-4 bg-[#0a0f1b]/50 border-b border-slate-800/60 overflow-x-auto no-scrollbar scroll-smooth touch-pan-x font-['Cairo']">
        <div className="flex items-center gap-3 min-w-max">
          {/* Add story button */}
          <button
            onClick={onOpenCreateStory}
            className="flex flex-col items-center gap-1.5 group select-none relative"
          >
            <div className="relative w-14 h-14 rounded-full p-0.5 border-2 border-dashed border-emerald-500/60 hover:border-emerald-400 transition flex items-center justify-center bg-[#121927]">
              <img
                src={currentUser.avatar}
                alt={currentUser.displayName}
                className="w-full h-full rounded-full object-cover opacity-80 group-hover:opacity-100 transition"
              />
              <div className="absolute -bottom-0.5 -left-0.5 w-5 h-5 rounded-full bg-emerald-500 text-[#080d16] flex items-center justify-center border-2 border-[#0a0f1b] shadow-md group-hover:scale-110 transition">
                <Plus className="w-3.5 h-3.5 stroke-[3]" />
              </div>
            </div>
            <span className="text-[11px] font-semibold text-slate-300 group-hover:text-emerald-300">
              حالتي
            </span>
          </button>

          {/* Stories from others */}
          {userGroups.map((group, idx) => (
            <button
              key={group.username}
              onClick={() => {
                setActiveGroupIndex(idx);
                setActiveStoryIndex(0);
                setProgress(0);
              }}
              className="flex flex-col items-center gap-1.5 group select-none"
            >
              <div className={`relative w-14 h-14 rounded-full p-[2.5px] hover:scale-105 transition shadow-lg ${group.stories.some(s => !viewedStories.has(s.id)) ? 'bg-gradient-to-tr from-emerald-400 via-teal-400 to-indigo-500 shadow-emerald-500/10' : 'bg-slate-700'}`}>
                <div className="w-full h-full rounded-full p-0.5 bg-[#0e1422]">
                  <img
                    src={group.userAvatar}
                    alt={group.displayName}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              </div>
              <span className="text-[11px] font-medium text-slate-300 group-hover:text-white max-w-[64px] truncate">
                {group.username === currentUser.username ? 'حالتي' : (group.displayName || group.username || 'مستخدم').split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* FULLSCREEN STORY VIEWER MODAL */}
      {currentStory && currentGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-2xl font-['Cairo'] select-none">
          {/* Close button */}
          <button
            onClick={closeViewer}
            className="absolute top-4 right-4 z-50 p-2.5 rounded-full bg-black/50 text-white hover:bg-white/20 transition"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Edit & Delete buttons (only if it's the current user's story) */}
          {currentStory.username === currentUser.username && (
            <div className="absolute top-4 left-4 z-50 flex items-center gap-2">
              {onEditStory && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPaused(true);
                    setEditingStory(currentStory);
                    setEditCaption(currentStory.caption || '');
                    setEditMood(currentStory.mood);
                    setEditMusic(currentStory.music || '');
                    soundEffects.playTap();
                  }}
                  className="p-2.5 rounded-2xl bg-black/60 backdrop-blur-md text-emerald-400 border border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/20 active:scale-95 shadow-lg shadow-black/40 transition flex items-center gap-1.5 text-xs font-bold"
                  title="تعديل القصة"
                >
                  <Edit3 className="w-4 h-4" />
                  <span className="hidden sm:inline">تعديل</span>
                </button>
              )}
              {onDeleteStory && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPaused(true);
                    setStoryToDelete(currentStory.id);
                    setShowConfirmDeleteModal(true);
                    soundEffects.playTap();
                  }}
                  className="p-2.5 rounded-2xl bg-black/60 backdrop-blur-md text-rose-400 border border-rose-500/30 hover:border-rose-500 hover:bg-rose-500/20 active:scale-95 shadow-lg shadow-black/40 transition flex items-center gap-1.5 text-xs font-bold"
                  title="حذف القصة"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">حذف</span>
                </button>
              )}
            </div>
          )}

          {/* Story Card Container */}
          <div className="relative w-full max-w-sm h-[85vh] rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-[#0a0f1b] flex flex-col justify-between">
            
            {/* Top segmented progress bar */}
            <div className="absolute top-3 left-3 right-3 z-30 flex gap-1.5">
              {currentGroup.stories.map((_, sIdx) => {
                let barWidth = '0%';
                if (sIdx < activeStoryIndex) barWidth = '100%';
                else if (sIdx === activeStoryIndex) barWidth = `${progress}%`;
                
                return (
                  <div key={sIdx} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
                    <div
                      className="h-full bg-white transition-all duration-75 ease-linear"
                      style={{ width: barWidth }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Top user header */}
            <div className="relative z-30 pt-7 px-4 pb-2 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
              <div className="flex items-center gap-2.5">
                <img
                  src={currentGroup.userAvatar}
                  alt={currentGroup.displayName}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-emerald-400"
                />
                <div>
                  <div className="text-xs font-bold text-white flex items-center gap-1">
                    <span>{currentGroup.displayName}</span>
                    <span className="text-[10px] text-slate-400 font-mono">@{currentGroup.username}</span>
                  </div>
                  {currentStory.music && (
                    <div className="flex items-center gap-1 text-[10px] text-emerald-400 font-medium">
                      <Music className="w-3 h-3 animate-spin" />
                      <span>{currentStory.music}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1 text-[11px] text-slate-300 bg-black/40 px-2 py-0.5 rounded-full">
                  <Eye className="w-3.5 h-3.5" />
                  <span>{currentStory.viewsCount}</span>
                </div>
                {currentStory.type === 'video' && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                    className="p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition"
                  >
                    {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>

            {/* Media content */}
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
              {currentStory.type === 'video' ? (
                <video
                  ref={videoRef}
                  src={currentStory.mediaUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  playsInline
                  muted={isMuted}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onEnded={handleVideoEnded}
                />
              ) : (
                <img
                  src={currentStory.mediaUrl}
                  alt="Story content"
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {/* Click navigation & Pause overlays */}
            <div className="absolute inset-0 z-20">
              <div 
                className="absolute top-0 bottom-0 right-0 w-1/2 cursor-pointer z-20" 
                onClick={(e) => { e.stopPropagation(); handleNextStory(); }} 
                onMouseDown={() => setIsPaused(true)}
                onMouseUp={() => setIsPaused(false)}
                onTouchStart={() => setIsPaused(true)}
                onTouchEnd={() => setIsPaused(false)}
              />
              <div 
                className="absolute top-0 bottom-0 left-0 w-1/2 cursor-pointer z-20" 
                onClick={(e) => { e.stopPropagation(); handlePrevStory(); }} 
                onMouseDown={() => setIsPaused(true)}
                onMouseUp={() => setIsPaused(false)}
                onTouchStart={() => setIsPaused(true)}
                onTouchEnd={() => setIsPaused(false)}
              />
            </div>

            {/* Bottom Caption & Reply Bar */}
            <div 
              className="relative z-30 p-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent flex flex-col items-center"
              onMouseDown={() => setIsPaused(true)}
              onMouseUp={() => setIsPaused(false)}
              onTouchStart={() => setIsPaused(true)}
              onTouchEnd={() => setIsPaused(false)}
            >
              {currentStory.caption && (
                <p className="text-sm font-semibold text-white mb-3 text-center drop-shadow-md bg-black/30 px-3 py-1.5 rounded-xl inline-block max-w-full break-words">
                  {currentStory.caption}
                </p>
              )}

              {currentStory.username !== currentUser.username && (
                <>
                  {/* Quick Reactions */}
                  <div className="flex justify-center gap-3 mb-3 w-full">
                    {['😂', '😍', '🔥', '👏', '😢', '💯'].map(emoji => (
                      <button 
                        key={emoji}
                        onClick={() => sendQuickReaction(emoji)}
                        className="text-2xl hover:scale-125 transition-transform drop-shadow-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 w-full">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="رد على القصة..."
                      className="flex-1 px-4 py-2.5 rounded-full bg-white/15 border border-white/20 text-xs text-white placeholder-slate-300 focus:outline-none focus:border-emerald-400 backdrop-blur-md transition"
                      onFocus={() => setIsPaused(true)}
                      onBlur={() => setIsPaused(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && replyText.trim()) {
                          onReplyStory?.(currentStory, replyText.trim());
                          setReplyText('');
                          closeViewer();
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (replyText.trim()) {
                          onReplyStory?.(currentStory, replyText.trim());
                          setReplyText('');
                          closeViewer();
                        }
                      }}
                      className="p-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#080d16] font-bold shadow-lg transition shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modern Story Editor Modal */}
      {editingStory && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none"
          onClick={() => {
            setEditingStory(null);
            setIsPaused(false);
          }}
        >
          <div
            className="relative w-full max-w-md bg-[#0e1422] border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 text-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <Edit3 className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-white">تعديل القصة</h3>
              </div>
              <button
                onClick={() => {
                  setEditingStory(null);
                  setIsPaused(false);
                }}
                className="p-1.5 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Media Preview & Caption */}
            <div className="flex gap-3">
              <div className="w-20 h-28 shrink-0 rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 relative">
                {editingStory.type === 'video' ? (
                  <video src={editingStory.mediaUrl} className="w-full h-full object-cover" />
                ) : (
                  <img src={editingStory.mediaUrl} alt="" className="w-full h-full object-cover" />
                )}
              </div>

              <div className="flex-1 flex flex-col">
                <label className="text-xs font-semibold text-slate-300 mb-1">نص القصة (الكابشن):</label>
                <textarea
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  maxLength={150}
                  placeholder="اكتب عبارة ملهمة لقصتك..."
                  rows={3}
                  className="w-full p-2.5 rounded-xl bg-[#141b27] border border-slate-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-xs text-white placeholder-slate-500 resize-none outline-none transition"
                />
                <span className="text-[10px] text-slate-500 text-left mt-1">
                  {editCaption.length}/150
                </span>
              </div>
            </div>

            {/* Mood selector */}
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1.5 block">طابع القصة والمزاج:</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: 'inspired', label: '✨ إلهام' },
                  { key: 'calm', label: '🌿 هادئ' },
                  { key: 'excited', label: '⚡ طاقة' },
                  { key: 'coffee', label: '☕ قهوة' },
                  { key: 'night', label: '🌙 ليلي' },
                  { key: 'love', label: '❤️ وتين' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setEditMood(editMood === item.label ? undefined : item.label)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
                      editMood === item.label
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300 shadow-sm'
                        : 'bg-[#141b27] border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Music track */}
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1.5 block">المقطع الصوتي المصاحب:</label>
              <div className="relative">
                <Music className="w-4 h-4 text-emerald-400 absolute right-3 top-3 pointer-events-none" />
                <input
                  type="text"
                  value={editMusic}
                  onChange={(e) => setEditMusic(e.target.value)}
                  placeholder="مثال: نغمات هادئة - وتين"
                  className="w-full pr-9 pl-3 py-2 rounded-xl bg-[#141b27] border border-slate-800 focus:border-emerald-500 text-xs text-white placeholder-slate-500 outline-none transition"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="pt-2 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setEditingStory(null);
                  setIsPaused(false);
                }}
                className="px-4 py-2 rounded-xl bg-[#141b27] hover:bg-slate-800 border border-slate-800 text-xs font-bold text-slate-300 transition"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={() => {
                  soundEffects.playTap();
                  setShowConfirmEditModal(true);
                }}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-xs font-bold text-white shadow-lg shadow-emerald-950/40 flex items-center gap-1.5 transition"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>حفظ التعديلات</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Edit Confirmation Modal */}
      {showConfirmEditModal && (
        <ConfirmationModal
          isOpen={showConfirmEditModal}
          type="edit_story"
          title="تأكيد تعديل القصة"
          description="هل أنت متأكد من حفظ التعديلات ونشر التحديثات لجميع المتابعين على وتين؟"
          confirmText="تأكيد وحفظ"
          isDestructive={false}
          onConfirm={() => {
            if (editingStory && onEditStory) {
              onEditStory(editingStory.id, {
                caption: editCaption,
                mood: editMood,
                music: editMusic,
              });
              soundEffects.playSent();
            }
            setShowConfirmEditModal(false);
            setEditingStory(null);
            setIsPaused(false);
          }}
          onCancel={() => setShowConfirmEditModal(false)}
        />
      )}

      {/* Story Delete Confirmation Modal */}
      {showConfirmDeleteModal && storyToDelete && (
        <ConfirmationModal
          isOpen={showConfirmDeleteModal}
          type="delete_story"
          title="حذف القصة نهائياً"
          description="هل أنت متأكد من رغبتك في حذف هذه القصة نهائياً؟ لن يتمكن أحد من مشاهدتها بعد الحذف."
          confirmText="نعم، احذف القصة"
          isDestructive={true}
          onConfirm={() => {
            if (onDeleteStory && storyToDelete) {
              onDeleteStory(storyToDelete);
              handleNextStory();
            }
            setShowConfirmDeleteModal(false);
            setStoryToDelete(null);
            setIsPaused(false);
          }}
          onCancel={() => {
            setShowConfirmDeleteModal(false);
            setStoryToDelete(null);
            setIsPaused(false);
          }}
        />
      )}
    </>
  );
};
